/**
 * Hook React pour gérer les "marques de lecture admin" sur n'importe quel
 * type d'entité (communication / invoice / question / client_document).
 *
 * Batch #14.93 — Permet à l'admin de marquer/démarquer manuellement une ligne
 * comme "lue" pour faire disparaître le badge non-lu sans attendre le client.
 *
 * Usage :
 *   const { isOverridden, markRead, markUnread, markAllRead, resetAll, count } =
 *     useReadOverrides("communication");
 *
 *   // dans une ligne :
 *   const reallyUnread = !row.read_at && !isOverridden(row.id);
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "@/api";
import { toast } from "@/lib/toast";

export function useReadOverrides(entityType) {
  const [ids, setIds] = useState(new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!entityType) { setLoading(false); return; }
    try {
      const r = await api.get(`/admin/read-overrides?entity_type=${entityType}`);
      setIds(new Set(r.data?.ids || []));
    } catch {
      // best-effort : si l'endpoint répond mal, on ignore (les badges restent comme avant)
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => { refresh(); }, [refresh]);

  const isOverridden = useCallback((id) => ids.has(id), [ids]);

  const markRead = useCallback(async (entityId) => {
    try {
      await api.post("/admin/read-overrides", { entity_type: entityType, entity_id: entityId });
      setIds((prev) => new Set(prev).add(entityId));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de marquer comme lu");
    }
  }, [entityType]);

  const markUnread = useCallback(async (entityId) => {
    try {
      await api.delete(`/admin/read-overrides/${entityType}/${entityId}`);
      setIds((prev) => {
        const next = new Set(prev);
        next.delete(entityId);
        return next;
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de marquer comme non lu");
    }
  }, [entityType]);

  const markAllRead = useCallback(async (entityIds) => {
    if (!entityIds || entityIds.length === 0) return;
    try {
      await api.post("/admin/read-overrides/bulk", {
        entity_type: entityType,
        entity_ids: entityIds,
      });
      setIds((prev) => {
        const next = new Set(prev);
        entityIds.forEach((id) => next.add(id));
        return next;
      });
      toast.success(`${entityIds.length} élément(s) marqué(s) comme lu(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du marquage en masse");
    }
  }, [entityType]);

  const resetAll = useCallback(async () => {
    try {
      const r = await api.delete(`/admin/read-overrides/reset?entity_type=${entityType}`);
      setIds(new Set());
      toast.success(`${r.data?.deleted || 0} marquage(s) réinitialisé(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec du reset");
    }
  }, [entityType]);

  return {
    loading,
    count: ids.size,
    isOverridden,
    markRead,
    markUnread,
    markAllRead,
    resetAll,
    refresh,
  };
}

export default useReadOverrides;
